<script lang="ts">
  // Accessible single-select combobox (WAI-ARIA combobox + listbox) that is
  // CONSTRAINED to the provided list: only a real breed can be selected. Typed
  // text that does not resolve to a listed breed never persists and never sets a
  // breedId, so nicknames and free text are rejected. Breed is optional, so
  // clearing the field (no selection) is valid.

  interface Breed {
    id: number
    name: string
  }
  interface Props {
    breeds: Breed[]
    breedId: number | null
    id?: string
    label?: string
  }
  let { breeds, breedId = $bindable(), id = 'animal-breed', label = 'Breed' }: Props = $props()

  let query = $state('')
  let open = $state(false)
  let activeIndex = $state(-1)

  const selected = $derived(breeds.find((b) => b.id === breedId) ?? null)

  // Reflect an externally set or restored selection into the input text (e.g.
  // navigating back to this step). Only while closed, so active typing is never
  // clobbered.
  $effect(() => {
    if (!open) query = selected?.name ?? ''
  })

  const listId = $derived(`${id}-listbox`)
  const optionId = (i: number) => `${id}-option-${i}`

  // While the query equals the current selection (or is empty) show the whole
  // list so the user can browse; once they start editing, filter by substring.
  const matches = $derived(
    query.trim() === '' || query === selected?.name
      ? breeds
      : breeds.filter((b) => b.name.toLowerCase().includes(query.trim().toLowerCase())),
  )

  function choose(b: Breed): void {
    breedId = b.id
    query = b.name
    open = false
    activeIndex = -1
  }

  function onInput(): void {
    open = true
    activeIndex = matches.length > 0 ? 0 : -1
    // Editing invalidates the current pick until a valid selection is committed.
    if (selected && query !== selected.name) breedId = null
  }

  // Resolve the field on close: an exact (case-insensitive) breed name selects
  // that breed; anything else reverts to the current selection or clears. This
  // is what forces a pick from the list.
  function commitOnClose(): void {
    const exact = breeds.find((b) => b.name.toLowerCase() === query.trim().toLowerCase())
    if (exact) {
      choose(exact)
      return
    }
    query = selected?.name ?? ''
    if (query === '') breedId = null
    open = false
    activeIndex = -1
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      open = true
      activeIndex = Math.min((activeIndex < 0 ? -1 : activeIndex) + 1, matches.length - 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      open = true
      activeIndex = Math.max(activeIndex - 1, 0)
    } else if (e.key === 'Enter') {
      const pick = activeIndex >= 0 ? matches[activeIndex] : undefined
      if (open && pick) {
        e.preventDefault()
        choose(pick)
      }
    } else if (e.key === 'Escape') {
      query = selected?.name ?? ''
      open = false
      activeIndex = -1
    }
  }
</script>

<div
  class="form-field"
  onfocusout={(e) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) commitOnClose()
  }}
>
  <label for={id}>{label}</label>
  <div class="combo">
    <input
      {id}
      role="combobox"
      aria-expanded={open}
      aria-controls={listId}
      aria-autocomplete="list"
      aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
      bind:value={query}
      placeholder="Search breeds…"
      autocomplete="off"
      oninput={onInput}
      onfocus={() => (open = true)}
      onkeydown={onKeydown}
    />
    <ul id={listId} role="listbox" aria-label="{label} suggestions" class="listbox" class:open>
      {#each matches as b, i (b.id)}
        <li
          id={optionId(i)}
          role="option"
          aria-selected={b.id === breedId}
          class:active={i === activeIndex}
          onmousedown={(e) => {
            e.preventDefault()
            choose(b)
          }}
        >
          {b.name}
        </li>
      {/each}
    </ul>
    {#if open && matches.length === 0}
      <p class="empty">No matching breed. Pick one from the list.</p>
    {/if}
  </div>
  <span class="hint">Select a breed from the list ({breeds.length} available).</span>
</div>

<style>
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
  }
  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .combo {
    position: relative;
  }
  input {
    width: 100%;
    padding: 10px 12px;
    min-height: 44px; /* comfortable touch target */
    box-sizing: border-box;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 1rem; /* 16px avoids iOS zoom-on-focus */
    background: var(--surface-2);
    color: var(--ink);
  }
  input:focus-visible {
    outline: 2px solid var(--link);
    outline-offset: 1px;
  }
  .listbox {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 20;
    margin: 2px 0 0;
    padding: 4px;
    list-style: none;
    max-height: 240px;
    overflow-y: auto;
    background: var(--surface-2);
    border: 1px solid var(--line-strong);
    border-radius: 6px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    display: none;
  }
  .listbox.open {
    display: block;
  }
  .listbox li {
    padding: 8px 10px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.95rem;
    color: var(--ink);
  }
  .listbox li.active,
  .listbox li:hover {
    background: var(--brand-tint);
    color: var(--link);
  }
  .listbox li[aria-selected='true'] {
    font-weight: 600;
  }
  .empty {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 20;
    margin: 2px 0 0;
    padding: 10px 12px;
    background: var(--surface-2);
    border: 1px solid var(--line-strong);
    border-radius: 6px;
    font-size: 0.9rem;
    color: var(--muted);
  }
  .hint {
    font-size: 0.75rem;
    color: var(--muted);
  }
</style>
